/**
 * src/components/map/useMapHover.ts
 *
 * The hover/pin lifecycle, wired to a clock.
 *
 * All the rules live in `mapHoverReducer`, which is pure. This adds the
 * two things a reducer cannot have: a timer to fire the grace period,
 * and an Escape key listener. The returned handle is referentially
 * stable so that map event registration — which is expensive and
 * global — does not tear down and rebuild every time the pointer moves
 * a pixel.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  initialHoverState,
  mapHoverReducer,
  type HoverTarget,
  type MapHoverEvent,
  type MapHoverState,
} from './mapHoverLifecycle';

export interface MapHoverActions<T> {
  enter: (payload: T, point: { x: number; y: number }) => void;
  move: (point: { x: number; y: number }) => void;
  leave: () => void;
  /** The pointer moved onto the card: hold it open. */
  cardEnter: () => void;
  cardLeave: () => void;
  /** A deliberate click. The only thing allowed to start loading. */
  pin: (payload: T, point: { x: number; y: number }) => void;
  unpin: () => void;
}

export interface MapHoverHandle<T> {
  hovered: HoverTarget<T> | null;
  pinned: HoverTarget<T> | null;
  /**
   * Referentially stable for the lifetime of the component.
   *
   * Kept apart from the state on purpose: registering map event
   * handlers is a global, expensive operation, and a handle that
   * changed identity on every pointer move would tear them down and
   * rebuild them at the frame rate of the mouse.
   */
  actions: MapHoverActions<T>;
}

export function useMapHover<T>(
  identify: (payload: T) => string | null,
): MapHoverHandle<T> {
  const [state, dispatch] = useReducer(
    mapHoverReducer as (s: MapHoverState<T>, e: MapHoverEvent<T>) => MapHoverState<T>,
    undefined,
    initialHoverState<T>,
  );

  const identifyRef = useRef(identify);
  identifyRef.current = identify;

  const target = useCallback((payload: T, point: { x: number; y: number }) => {
    const id = identifyRef.current(payload);
    return id ? { id, payload, point } : null;
  }, []);

  // One timer, rescheduled as the deadline moves. Firing a `tick` rather
  // than clearing directly keeps every transition in the reducer, so the
  // tested rules are the rules that run.
  const dismissAt = state.dismissAt;
  useEffect(() => {
    if (dismissAt === null) return undefined;
    const delay = Math.max(0, dismissAt - Date.now());
    const timer = setTimeout(() => dispatch({ type: 'tick', at: Date.now() }), delay);
    return () => clearTimeout(timer);
  }, [dismissAt]);

  // Escape closes the pinned card. Registered only while something is
  // pinned, so it cannot swallow Escape from a dialog elsewhere.
  const hasPinned = !!state.pinned;
  useEffect(() => {
    if (!hasPinned) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch({ type: 'escape' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPinned]);

  const actions = useMemo<MapHoverActions<T>>(() => ({
    enter: (payload: T, point: { x: number; y: number }) => {
      const t = target(payload, point);
      if (t) dispatch({ type: 'enter', target: t, at: Date.now() });
    },
    move: (point: { x: number; y: number }) => dispatch({ type: 'move', point }),
    leave: () => dispatch({ type: 'leave', at: Date.now() }),
    cardEnter: () => dispatch({ type: 'cardEnter' }),
    cardLeave: () => dispatch({ type: 'cardLeave', at: Date.now() }),
    pin: (payload: T, point: { x: number; y: number }) => {
      const t = target(payload, point);
      if (t) dispatch({ type: 'pin', target: t });
    },
    unpin: () => dispatch({ type: 'unpin' }),
  }), [target]);

  return useMemo(
    () => ({ hovered: state.hovered, pinned: state.pinned, actions }),
    [state.hovered, state.pinned, actions],
  );
}
